import React from "react";
import {
  Grid,
  Button,
  Popover,
  DialogContent,
  DialogActions,
  DialogTitle,
  Checkbox
} from "@material-ui/core";
import styled from "styled-components";
import { observer } from "mobx-react";
// import ExtjsCodeMirror from "app/common/components/input/ExtjsCodeMirror";
import SelectSheetPopup from "app/common/components/SelectSheetPopup";
import SelectMultiLevel from "app/common/components/input/Menu/SelectMultiLevel";
import ExtjsCriteriaPanel from "app/common/components/input/ExtjsCriteriaPanel";
import CriteriaPanel from "../../common/Criteria/CriteriaPanel";
import { toJS } from "mobx";

const COLUMN_TYPES = [
  { id: "text", name: "Text" },
  { id: "email", name: "Email", column_type_id: 14 },
  { id: "number", name: "Number", column_type_id: 5 },
  { id: "date", name: "Date", column_type_id: 4},
  {id: "datetime", name: "DateTime", column_type_id: 16},
  { id: "currency", name: "Currency", column_type_id: 17 },
  { id: "file", name: "File", column_type_id: 9 },
  { id: "barcode", name: "Barcode", column_type_id: 18 },
  { id: "percentage", name: "Percentage", column_type_id: 22 },
];

function LookupPopup({
  anchorEl,
  onClose,
  onSave,
  values: {
    column_type_id: columnTypeId,
    options: {
      source_list_id: sourceListId,
      source_column_id: sourceColumnId,
      column_type: prevColumnType = "text",
      query_string: queryString = "",
    },
  },
  store: { allLists, fetchListColumns, getCurrentListColumns, getCurrentListFormattedColumns, currentListId, column },
  lookupCriteria,
}) {
  const [selectedSheetId, setSelectedSheetId] = React.useState(sourceListId);
  const [selectedColumnId, setSelectedColumnId] = React.useState(
    sourceColumnId
  );
  const [columnType, setColumnType] = React.useState(prevColumnType);
  const selectedSheet =
    allLists.find((sheet) => sheet.id === selectedSheetId) ?? {};
  const [sheetColumns, setSheetColumns] = React.useState([]);
  const [currentListFormattedColumns, setCurrentListFormattedColumns] = React.useState([]);
  const [isCriteriaOpen, setIsCriteriaOpen] = React.useState(false);
  const [newCriteria, setNewCriteria] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isOptionsShown, showOptions] = React.useState(false);
  const [isColumnUpdatable,setIsColumnUpdatable] = React.useState(column?.options?.is_column_updatable ?? true);
  const handleChange = React.useCallback(
    (val) => {
      setNewCriteria(val);
    },
    [setNewCriteria]
  );
  const handleSaveButtonClick = async() => {
    if (!selectedSheetId || !selectedColumnId || !columnType) {
      return Ext.Msg.alert("Error", "Please fill all details");
    }
    setNewCriteria(newCriteria);
    onSave({
      source_list_id: selectedSheetId,
      source_column_id: selectedColumnId,
      column_type: columnType,
      is_column_updatable: isColumnUpdatable,
      query_string: columnTypeId === "10-1" ? "UserAccounts.status = 'ACTIVE'" : newCriteria ,
      is_user_type: columnTypeId === "10-1",
    });
  };

  const columnTypeHandler = (type, showError = true) => {
    if (columnType != "percentage" && type == "percentage") {
      showError && showAlert('Cannot set "percentage" as column type for this Column', false );
      return;
    }
    if (columnType == "percentage" && type != "percentage") {
      showError && showAlert('Only "percentage" as column type is supported for this Column', false );
      return;
    }
    if (columnType != "file" && type == "file") {
      showError && showAlert('Cannot set "file" as column type for this Column', false );
      return;
    }
    if(columnType == "file" && type!= "file"){
      showError && showAlert('Only "file" as column type is supported for this Column', false );
      return;
    }
    if (
      (columnType != "date" && type == "date") ||
      (columnType != "datetime" && type == "datetime")
    ) {
      if (
        (columnType == "date" && type == "datetime") ||
        (columnType == "datetime" && type == "date")
      ) {
        setColumnType(type);
        return;
      }    
      showError && showAlert(`Cannot set "${type}" as column type for this Column`, false );
      return;
    }
    
    if (
      (columnType == "date" && type != "date") ||
      (columnType == "datetime" && type != "datetime")
    ) {
      showError && showAlert(`Only "${columnType}" as column type is supported for this Column`, false );
      return;
    }
    
    setColumnType(type);
  };

  const showAlert = (msg, type) => {
    window.appStore.notificationStore.showNotification(msg, type ? "success" : "error");
  };

  const fetchSourceSheetColumns =  async (selectedSheetId) => {
    const sheetResp = await fetchListColumns(selectedSheetId);
    if (sheetResp) {
      let sourceSheetColumns = await TL.list.settings.lookup.CriteriaHelper.formatListColumns(sheetResp.listData, sheetResp.list.name); 
      sourceSheetColumns = sourceSheetColumns.filter(col => col.default_type == 0);
      return sourceSheetColumns;
    }
    return [];
  }

  React.useEffect(() => {
    (async function () {
      if (currentListFormattedColumns.length == 0) {
        setCurrentListFormattedColumns(getCurrentListFormattedColumns());
      }
      setSheetColumns([]);
      columnTypeHandler(columnType ? columnType : "text", false);
      let sheetColumns = [];
      if (selectedSheetId) {
        setIsLoading(true);
        sheetColumns = await fetchSourceSheetColumns(selectedSheetId);
        setIsLoading(false);
      }

      setSheetColumns(sheetColumns);
    })();
  }, [selectedSheetId, fetchListColumns]);

  return (
    <PopoverStyled
      anchorOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      open={Boolean(anchorEl)}
      onClose={onClose}
      anchorEl={anchorEl}
      maxWidth="xl"
    >
      <DialogTitle>Describe your lookup column.</DialogTitle>
      <StyledDialogContent>
        <Grid container>
          <Grid item xs={12}>
            Select a source sheet
          </Grid>
          <Grid
            item
            container
            direction="row"
            justify="flex-start"
            alignItems="center"
            xs={12}
          >
            {selectedSheet.name ?? ""}
            <SelectSheetPopup
              sheets={allLists}
              LabelComponent={
                <Button component="a" disabled={columnTypeId === "10-1"}>
                  {selectedSheet.name ? "Change" : "Click Here"}
                </Button>
              }
              selectedSheet={selectedSheet} //this.props.selectedSheet
              onChange={(sheet) => {
                setSelectedSheetId(sheet.id);
                setSelectedColumnId();
              }}
              isLookupPopup={true}
            />
          </Grid>
          {selectedSheet.name && (
            <>
              <Grid container item spacing={1}>
                <Grid item>
                  <SelectMultiLevel
                    // className="select_multilevel"
                    values={sheetColumns}
                    valueKey={"alias_path"}
                    displayKey={"name"}
                    value={sheetColumns.find((i) => i.id == selectedColumnId)}
                    label={"Select a column to use"}
                    onChange={(val) => {
                      const columnTypeId = parseInt(val.column_type_id);
                      const newColumnType = COLUMN_TYPES.find(
                        (type) =>
                          type.column_type_id === columnTypeId ||
                          type.column_type_ids?.find(
                            (x) => x === columnTypeId
                          ) !== undefined
                      );
                      newColumnType?.id !== columnType &&
                        setColumnType(newColumnType?.id || "text");
                      setSelectedColumnId(val.id);
                    }}
                    isLoading={isLoading}
                    isAddColumnLookupPopup={true}
                  />
                </Grid>
                <Grid
                  item
                  xs={5}
                  style={{ display: "flex" }}
                  alignItems="center"
                >
                  {isOptionsShown && (
                    <>
                      Data Type
                      <Grid item>
                        <SelectMultiLevel
                          // className="select_multilevel"
                          values={COLUMN_TYPES}
                          valueKey={"id"}
                          displayKey={"name"}
                          label={"Column Type"}
                          value={COLUMN_TYPES.find((i) => i.id === columnType)}
                          // label={"operation"}
                          onChange={(val) => columnTypeHandler(val.id)}
                        />
                      </Grid>
                    </>
                  )}
                  {!isOptionsShown && selectedColumnId && (
                    <Button
                      component="a"
                      onClick={() => showOptions(!isOptionsShown)}
                    >
                      + options
                    </Button>
                  )}
                </Grid>
              </Grid>
              <Button
                component="a"
                onClick={() => setIsCriteriaOpen(!isCriteriaOpen)}
              >
                {!isCriteriaOpen ? "[+] Add Condition?" : "[-] Select Criteria"}
              </Button>
              {isCriteriaOpen && (
                <CriteriaPanel
                  onChange={handleChange}
                  value={
                    typeof lookupCriteria === "object"
                      ? toJS(lookupCriteria)
                      : lookupCriteria
                  }
                  lookupColumns={sheetColumns}
                  lookupListId={selectedSheetId}
                  currentSheetColumns={currentListFormattedColumns}
                  currentListId={currentListId}
                  hiddenMultiLevels={false}
                  isColumnTypeDisabled={false}
                  floatingCriteriaButton
                />
              )}
            </>
          )}
          <Grid 
            container
            alignItems="center"
            >
            <Grid item>
              <Checkbox
                checked={isColumnUpdatable}
                onChange={(event) => {
                  setIsColumnUpdatable(event.target.checked ? true : false);
                }}
              />
            </Grid>
            <StyledTextContent 
              item
              onClick={()=>{
              setIsColumnUpdatable(!isColumnUpdatable);
              }}
            >
              Update if the column is modified in the source sheet
            </StyledTextContent>
          </Grid>
        </Grid>
      </StyledDialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSaveButtonClick}
          color="primary"
        >
          Save
        </Button>
      </DialogActions>
    </PopoverStyled>
  );
}

const PopoverStyled = styled(({ className, ...props }) => (
  <Popover {...props} classes={{ paper: className }} />
))`
  && {
    max-width: 800px;
    min-width: 730px;
    width: 100%;
  }
`;

const StyledDialogContent = styled(DialogContent)`
  max-height: 40vh;
`;

const StyledTextContent = styled(Grid)`
  & {
    cursor: default;
    &:hover {
      cursor: pointer;
    }
  }
`;

export default observer(LookupPopup);